import { useState, useEffect, useRef } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';
import { Separator } from '@/components/ui/separator';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ScrollArea } from '@/components/ui/scroll-area';
import { verifyPortalToken } from '@/lib/api';
import { API_BASE } from '@/lib/api';
import {
  Upload, CheckCircle2, AlertCircle, FileText, Loader2,
  Clock, XCircle, AlertTriangle, ChevronRight, CreditCard, Lock, RefreshCw
} from 'lucide-react';
import { BrandLogoDark } from '@/components/BrandLogo';
import { toast } from 'sonner';

const docStatusConfig = {
  valid: { icon: CheckCircle2, color: 'text-[hsl(142,56%,55%)]', bg: 'bg-[hsl(142_56%_15%)]', label: 'Valid' },
  pending_review: { icon: Clock, color: 'text-[hsl(36,90%,55%)]', bg: 'bg-[hsl(36_100%_15%)]', label: 'Under Review' },
  uploaded: { icon: Upload, color: 'text-[hsl(197,78%,55%)]', bg: 'bg-[hsl(197_80%_15%)]', label: 'Uploaded' },
  expired: { icon: AlertTriangle, color: 'text-[hsl(0,72%,60%)]', bg: 'bg-[hsl(0_85%_15%)]', label: 'Expired' },
  invalid: { icon: XCircle, color: 'text-[hsl(0,72%,60%)]', bg: 'bg-[hsl(0_85%_15%)]', label: 'Invalid' },
  missing: { icon: AlertCircle, color: 'text-[hsl(36,90%,55%)]', bg: 'bg-[hsl(36_100%_15%)]', label: 'Missing' },
};

export default function VendorPortalPage() {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [uploading, setUploading] = useState(null);
  const [uploadDocType, setUploadDocType] = useState('');
  const [paywall, setPaywall] = useState(null); // null=checking, false=no paywall, {requires_payment, amount, ...}
  const [noAssignmentWall, setNoAssignmentWall] = useState(false); // Alert wall for unassigned vendors
  const [demoLimitWall, setDemoLimitWall] = useState(false); // Demo tier 5-vendor limit wall
  const [paymentProcessing, setPaymentProcessing] = useState(false);
  const [wcExempt, setWcExempt] = useState(false);
  const [claimingExemption, setClaimingExemption] = useState(false);
  const fileInputRef = useRef(null);

  // Force dark mode on vendor portal
  useEffect(() => {
    const root = document.documentElement;
    const prev = root.classList.contains('dark') ? 'dark' : 'light';
    root.classList.remove('light');
    root.classList.add('dark');
    return () => { root.classList.remove('dark'); root.classList.add(prev); };
  }, []);


  const load = async () => {
    try {
      // First, load portal data to check assignment status
      const { data: d } = await verifyPortalToken(token);
      setData(d);
      setWcExempt(d?.vendor?.workers_comp_exempt || false);
      
      // Check if vendor has property assignments
      const hasAssignments = d?.properties && d.properties.length > 0;
      if (!hasAssignments) {
        setNoAssignmentWall(true);
        setLoading(false);
        return; // Show assignment alert wall
      }
      
      // Check Limited Demo 5-vendor limit (BEFORE payment check)
      if (d?.company_plan_tier === 'limited_demo') {
        console.log('Demo account detected - checking vendor limit...');
        
        // Check if this vendor already has documents
        const vendorHasDocs = d?.submitted_documents && d.submitted_documents.length > 0;
        console.log('Vendor has docs:', vendorHasDocs, 'Submitted docs:', d?.submitted_documents?.length);
        
        if (!vendorHasDocs) {
          // Vendor has no docs yet - check if limit reached
          try {
            console.log('Calling check-demo-limit endpoint...');
            const limitResp = await fetch(`${API_BASE}/portal/check-demo-limit/${token}`);
            const limitCheck = await limitResp.json();
            console.log('Demo limit check response:', limitCheck);
            
            if (limitCheck.limit_reached) {
              console.log('✋ Demo limit reached - showing wall');
              setDemoLimitWall(true);
              setLoading(false);
              return; // Show demo limit wall - BLOCKS before payment check
            } else {
              console.log('✅ Demo limit not reached - continuing to portal');
            }
          } catch (err) {
            console.error('Demo limit check failed:', err);
            // Continue anyway if check fails
          }
        } else {
          console.log('Vendor already has docs - skipping limit check');
        }
      } else {
        console.log('Not a demo account, plan tier:', d?.company_plan_tier);
      }
      
      // Check if vendor needs to pay (only after assignment and limit checks)
      try {
        const payResp = await fetch(`${API_BASE}/portal/payment-check/${token}`);
        const payCheck = await payResp.json();
        if (payCheck.requires_payment) {
          // If returning from successful payment, check session
          const paymentStatus = searchParams.get('payment');
          const sessionId = searchParams.get('session_id');
          if (paymentStatus === 'success' && sessionId) {
            try {
              await fetch(`${API_BASE}/portal/payment-status/${token}?session_id=${sessionId}`);
              // Payment confirmed, continue to portal
            } catch {}
          } else {
            setPaywall(payCheck);
            setLoading(false);
            return; // Show paywall, don't load portal data
          }
        }
      } catch { /* payment check failed, allow access */ }

    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail || '';
      if (status === 410) setError(detail || 'This link has expired.');
      else if (status === 404) setError(detail || 'Invalid link.');
      else setError('Unable to access portal. Please contact your property manager.');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [token]);

  const handleUpload = async (docType) => {
    setUploadDocType(docType);
    fileInputRef.current?.click();
  };

  const onFileSelected = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setUploading(uploadDocType);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('document_type', uploadDocType);
      
      const resp = await fetch(`${API_BASE}/portal/upload/${token}`, {
        method: 'POST',
        body: formData,
      });
      
      if (!resp.ok) {
        const err = await resp.json();
        throw new Error(err.detail || 'Upload failed');
      }
      
      const result = await resp.json();
      toast.success(`${file.name} uploaded successfully! AI processing started.`);
      
      // Reload portal data
      setTimeout(() => load(), 1500);
    } catch (err) {
      toast.error(err.message || 'Upload failed. Please try again.');
    } finally {
      setUploading(null);
      e.target.value = '';
    }
  };

  const handleWcExemptionToggle = async (checked) => {
    setClaimingExemption(true);
    try {
      const resp = await fetch(`${API_BASE}/portal/claim-wc-exemption/${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ is_exempt: checked })
      });
      
      if (!resp.ok) throw new Error('Failed to update exemption status');
      
      setWcExempt(checked);
      toast.success(checked ? 'Workers comp exemption claimed. Please upload exemption certificate.' : 'Workers comp exemption removed.');
      setTimeout(() => load(), 1000);
    } catch (err) {
      toast.error('Failed to update exemption status');
    } finally {
      setClaimingExemption(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background p-6">
        <div className="max-w-2xl mx-auto space-y-6">
          <Skeleton className="h-12 w-48" /><Skeleton className="h-64 rounded-xl" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6">
        <Card className="max-w-md w-full rounded-xl shadow-[var(--shadow-soft)]">
          <CardContent className="p-8 text-center">
            <AlertCircle className="mx-auto h-12 w-12 text-destructive mb-4" />
            <h2 className="text-lg font-semibold mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Access Denied</h2>
            <p className="text-sm text-muted-foreground">{error}</p>
            <p className="text-xs text-muted-foreground mt-4">If you need a new link, please contact your property manager.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ─── NO ASSIGNMENT ALERT WALL ───────────────────────────
  if (noAssignmentWall) {
    return (
      <div data-testid="vendor-portal-no-assignment" className="min-h-screen bg-background flex items-center justify-center p-6">
        <Card className="max-w-md w-full rounded-xl shadow-[var(--shadow-pop)] border-[hsl(36,90%,50%)]">
          <CardContent className="p-8 text-center">
            <div className="flex h-16 w-16 mx-auto items-center justify-center rounded-full bg-[hsl(36,90%,50%)]/10 mb-5">
              <AlertTriangle className="h-8 w-8 text-[hsl(36,90%,50%)]" />
            </div>
            <h2 className="text-xl font-bold mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Property Assignment Required</h2>
            <p className="text-sm text-muted-foreground mb-6">
              You are not currently assigned to any properties. Before you can upload compliance documents, your property manager must assign you to at least one property.
            </p>
            <div className="rounded-xl border border-border bg-muted/30 p-4 mb-6">
              <h3 className="text-sm font-semibold mb-2">Why is this required?</h3>
              <p className="text-xs text-muted-foreground leading-relaxed">
                Compliance requirements (insurance coverage, certifications, etc.) are specific to each property. Once you're assigned to a property, you'll be able to see exactly what documents are required and upload them through this portal.
              </p>
            </div>
            <div className="space-y-3 text-left mb-6">
              <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Next Steps:</h3>
              {[
                'Contact your property manager to assign you to a property',
                'Once assigned, refresh this page to access the portal',
                'You\'ll then be able to upload all required documents',
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-2 text-sm">
                  <div className="flex h-5 w-5 items-center justify-center rounded-full bg-primary/10 shrink-0">
                    <span className="text-[10px] font-semibold text-primary">{i + 1}</span>
                  </div>
                  <span>{item}</span>
                </div>
              ))}
            </div>
            <Button className="w-full" variant="outline" size="lg" onClick={() => window.location.reload()} data-testid="vendor-assignment-refresh-button">
              <RefreshCw className="mr-2 h-4 w-4" />
              Refresh Page
            </Button>
            <p className="text-[10px] text-muted-foreground mt-4">
              Vendor: <strong>{data?.vendor?.name || 'Unknown'}</strong> • Company: <strong>{data?.company_name || 'Unknown'}</strong>
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ─── DEMO LIMIT WALL ───────────────────────────
  if (demoLimitWall) {
    return (
      <div data-testid="vendor-portal-demo-limit" className="min-h-screen bg-background flex items-center justify-center p-6">
        <Card className="max-w-md w-full rounded-xl shadow-[var(--shadow-pop)] border-destructive">
          <CardContent className="p-8 text-center">
            <div className="flex h-16 w-16 mx-auto items-center justify-center rounded-full bg-destructive/10 mb-5">
              <AlertTriangle className="h-8 w-8 text-destructive" />
            </div>
            <h2 className="text-xl font-bold mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>PM Limit Reached</h2>
            <p className="text-sm text-muted-foreground mb-6">
              Your property manager's demo account is limited to <strong>5 vendors with documentation</strong>. This limit has been reached.
            </p>
            <div className="rounded-xl border border-border bg-muted/30 p-4 mb-6">
              <h3 className="text-sm font-semibold mb-2">Why am I seeing this?</h3>
              <p className="text-xs text-muted-foreground leading-relaxed">
                Your property manager is using VendorSuite's free 30-day trial, which allows up to 5 vendors to upload compliance documents. To enable more vendors, they need to upgrade to a paid plan.
              </p>
            </div>
            <div className="space-y-3 text-left mb-6">
              <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Next Steps:</h3>
              {[
                'Contact your property manager about upgrading',
                'Professional plan: Unlimited vendors with documents',
                'Enterprise plan: Unlimited vendors + properties',
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-2 text-sm">
                  <div className="flex h-5 w-5 items-center justify-center rounded-full bg-primary/10 shrink-0">
                    <span className="text-[10px] font-semibold text-primary">{i + 1}</span>
                  </div>
                  <span>{item}</span>
                </div>
              ))}
            </div>
            <p className="text-[10px] text-muted-foreground mt-4">
              Vendor: <strong>{data?.vendor?.name || 'Unknown'}</strong> • Company: <strong>{data?.company_name || 'Unknown'}</strong>
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ─── VENDOR-PAID PAYWALL ───────────────────────────
  if (paywall && paywall.requires_payment) {
    const handlePayment = async () => {
      setPaymentProcessing(true);
      try {
        const resp = await fetch(`${API_BASE}/portal/payment/${token}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ origin_url: window.location.origin }),
        });
        const data = await resp.json();
        if (data.url) {
          window.location.href = data.url;
        } else {
          toast.error('Payment setup failed');
        }
      } catch { toast.error('Payment failed'); }
      finally { setPaymentProcessing(false); }
    };

    return (
      <div data-testid="vendor-portal-paywall" className="min-h-screen bg-background flex items-center justify-center p-6">
        <Card className="max-w-md w-full rounded-xl shadow-[var(--shadow-pop)]">
          <CardContent className="p-8 text-center">
            <div className="flex h-16 w-16 mx-auto items-center justify-center rounded-full bg-primary/10 mb-5">
              <Lock className="h-8 w-8 text-primary" />
            </div>
            <h2 className="text-xl font-bold mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Compliance Portal Access</h2>
            <p className="text-sm text-muted-foreground mb-6">
              Your property manager requires a one-time annual compliance fee to access this portal and upload documents.
            </p>
            <Card className="rounded-xl border-primary/20 bg-primary/5 mb-6">
              <CardContent className="p-5">
                <div className="flex items-baseline justify-center gap-1 mb-1">
                  <span className="text-4xl font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>$50</span>
                  <span className="text-muted-foreground">/year</span>
                </div>
                <p className="text-xs text-muted-foreground">One-time payment • 12 months of access</p>
              </CardContent>
            </Card>
            <div className="space-y-3 text-left mb-6">
              {[
                'Upload compliance documents (COIs, W-9s, licenses)',
                'Track your compliance status in real-time',
                'Receive automated reminders for expiring documents',
                'Access for 12 months from date of payment',
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-2 text-sm">
                  <CheckCircle2 className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                  <span>{item}</span>
                </div>
              ))}
            </div>
            <Button className="w-full" size="lg" onClick={handlePayment} disabled={paymentProcessing} data-testid="vendor-paywall-pay-button">
              {paymentProcessing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CreditCard className="mr-2 h-4 w-4" />}
              {paymentProcessing ? 'Redirecting to payment...' : 'Pay $50 — Access Portal'}
            </Button>
            <p className="text-[10px] text-muted-foreground mt-4">Secure payment via Stripe. Your property manager has enabled vendor-paid billing for this portal.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const cs = data?.compliance_summary || {};
  const vendor = data?.vendor || {};
  const required = data?.required_documents || [];

  return (
    <div className="min-h-screen bg-background" data-testid="vendor-portal-page">
      {/* Hidden file input */}
      <input ref={fileInputRef} type="file" accept=".pdf,.png,.jpg,.jpeg,.tiff" className="hidden" onChange={onFileSelected} />

      {/* Header */}
      <div className="border-b border-border bg-card">
        <div className="max-w-3xl mx-auto px-6 py-6">
          <div className="flex items-center gap-3 mb-3">
            <BrandLogoDark width={180} />
          </div>
          <p className="text-sm text-muted-foreground">Compliance portal for <strong>{data?.company_name}</strong></p>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-6 py-8 space-y-6">
        {/* Vendor Info + Score */}
        <Card data-testid="vendor-portal-status-summary" className="rounded-xl shadow-[var(--shadow-soft)]">
          <CardContent className="p-6">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h1 className="text-xl font-semibold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>{vendor.name}</h1>
                <p className="text-sm text-muted-foreground capitalize">{vendor.vendor_type?.replace('_', ' ')}</p>
              </div>
              <div className="text-right">
                <p className="text-3xl font-bold tabular-nums" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>{vendor.compliance_score}%</p>
                <p className="text-xs text-muted-foreground">Compliance Score</p>
              </div>
            </div>
            
            <Progress value={cs.completion_pct || 0} className="h-2.5 mb-3" />
            
            <div className="flex items-center justify-between text-sm">
              <div className="flex gap-4">
                <span className="flex items-center gap-1.5">
                  <CheckCircle2 className="h-4 w-4 text-[hsl(142,56%,36%)]" />
                  <span>{cs.valid || 0} valid</span>
                </span>
                <span className="flex items-center gap-1.5">
                  <AlertCircle className="h-4 w-4 text-[hsl(36,90%,50%)]" />
                  <span>{cs.missing || 0} missing</span>
                </span>
                {cs.expired > 0 && (
                  <span className="flex items-center gap-1.5">
                    <AlertTriangle className="h-4 w-4 text-destructive" />
                    <span>{cs.expired} expired</span>
                  </span>
                )}
              </div>
              <Badge variant={cs.completion_pct === 100 ? 'default' : 'secondary'} className="text-xs">
                {cs.submitted || 0} of {cs.total_required || 0} submitted
              </Badge>
            </div>
          </CardContent>
        </Card>

        {/* Required Documents Checklist */}
        <Card data-testid="vendor-portal-required-docs-list" className="rounded-xl shadow-[var(--shadow-soft)]">
          <CardHeader className="pb-3">
            <CardTitle className="text-base" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Required Documents</CardTitle>
            <CardDescription>Upload each required document to become compliant</CardDescription>
            
            {/* Workers Comp Exemption Toggle */}
            {required.some(doc => doc.document_type === 'workers_comp' || doc.document_type === 'wc_exemption_cert') && (
              <div className="mt-4 p-3 rounded-lg border border-border bg-muted/30">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium">Workers Compensation Exemption</p>
                    <p className="text-xs text-muted-foreground">Check this if you are exempt from workers comp (e.g., sole proprietor)</p>
                  </div>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={wcExempt}
                      onChange={(e) => handleWcExemptionToggle(e.target.checked)}
                      disabled={claimingExemption}
                      className="h-4 w-4 rounded border-border"
                      data-testid="wc-exemption-toggle"
                    />
                    <span className="text-sm">I am WC exempt</span>
                  </label>
                </div>
                {wcExempt && (
                  <div className="mt-2 p-2 rounded bg-[hsl(36,90%,50%)]/10 border border-[hsl(36,90%,50%)]/20">
                    <p className="text-xs text-[hsl(36,90%,50%)]">
                      ⚠️ You must upload a Workers Comp Exemption Certificate to prove exemption status.
                    </p>
                  </div>
                )}
              </div>
            )}
          </CardHeader>
          <CardContent className="space-y-3">
            {required.map((doc, i) => {
              const statusInfo = docStatusConfig[doc.status] || docStatusConfig.missing;
              const StatusIcon = statusInfo.icon;
              const isUploading = uploading === doc.document_type;
              
              return (
                <div key={i} className={`flex items-center justify-between p-4 rounded-xl border ${doc.is_valid ? 'border-[hsl(142_40%_30%)] bg-[hsl(142_56%_10%)]' : doc.is_expired ? 'border-[hsl(0_60%_30%)] bg-[hsl(0_72%_10%)]' : 'border-border bg-card'} transition-colors duration-200`}>
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className={`flex h-10 w-10 items-center justify-center rounded-lg ${statusInfo.bg} shrink-0`}>
                      <StatusIcon className={`h-5 w-5 ${statusInfo.color}`} />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium">{doc.name}</p>
                      <div className="flex items-center gap-2 mt-0.5">
                        <Badge variant="outline" className="text-xs uppercase">{doc.document_type}</Badge>
                        {doc.filename && <span className="text-xs text-muted-foreground truncate max-w-[150px]">{doc.filename}</span>}
                        {doc.expires_at && (
                          <span className={`text-xs ${doc.is_expired ? 'text-destructive' : doc.is_expiring_soon ? 'text-[hsl(36,90%,50%)]' : 'text-muted-foreground'}`}>
                            {doc.is_expired ? 'Expired' : doc.is_expiring_soon ? 'Expiring soon' : `Exp: ${new Date(doc.expires_at).toLocaleDateString()}`}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2 shrink-0 ml-4">
                    <Badge variant="outline" className={`text-xs ${statusInfo.color}`}>{statusInfo.label}</Badge>
                    {(!doc.is_valid || doc.is_expired) && (
                      <Button
                        size="sm"
                        variant={doc.status === 'missing' ? 'default' : 'outline'}
                        data-testid={`portal-upload-${doc.document_type}`}
                        disabled={isUploading}
                        onClick={() => handleUpload(doc.document_type)}
                      >
                        {isUploading ? <Loader2 className="mr-1.5 h-3 w-3 animate-spin" /> : <Upload className="mr-1.5 h-3 w-3" />}
                        {isUploading ? 'Uploading...' : doc.is_expired ? 'Replace' : 'Upload'}
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
            
            {required.length === 0 && (
              <div className="py-8 text-center">
                <CheckCircle2 className="mx-auto h-10 w-10 text-[hsl(142,56%,36%)] mb-3" />
                <p className="text-sm text-muted-foreground">No documents required at this time</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Upload Additional Document */}
        <Card className="rounded-xl shadow-[var(--shadow-soft)]">
          <CardHeader className="pb-3">
            <CardTitle className="text-base" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Upload Additional Document</CardTitle>
            <CardDescription>Submit any other compliance document</CardDescription>
          </CardHeader>
          <CardContent>
            <UploadZone token={token} onSuccess={() => { toast.success('Document uploaded!'); setTimeout(() => load(), 1500); }} />
          </CardContent>
        </Card>

        {/* Submitted Documents History */}
        {data?.submitted_documents?.length > 0 && (
          <Card className="rounded-xl shadow-[var(--shadow-soft)]">
            <CardHeader className="pb-3">
              <CardTitle className="text-base" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Submission History</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <ScrollArea className="max-h-[300px]">
                {data.submitted_documents.slice(0, 10).map((doc, i) => (
                  <div key={i} className="flex items-center justify-between px-6 py-3 border-b border-border last:border-0">
                    <div className="flex items-center gap-3">
                      <FileText className="h-4 w-4 text-muted-foreground" />
                      <div>
                        <p className="text-sm truncate max-w-[250px]">{doc.filename || 'Untitled'}</p>
                        <p className="text-xs text-muted-foreground">{doc.created_at ? new Date(doc.created_at).toLocaleDateString() : ''}</p>
                      </div>
                    </div>
                    <Badge variant="secondary" className="text-xs uppercase">{doc.document_type}</Badge>
                  </div>
                ))}
              </ScrollArea>
            </CardContent>
          </Card>
        )}

        {/* Footer */}
        <div className="text-center py-4">
          <p className="text-xs text-muted-foreground">
            This portal link expires on {data?.token_expires_at ? new Date(data.token_expires_at).toLocaleDateString() : 'N/A'}.
            Contact your property manager if you need assistance.
          </p>
        </div>
      </div>
    </div>
  );
}

// Standalone upload zone component
function UploadZone({ token, onSuccess }) {
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [docType, setDocType] = useState('other');
  const fileRef = useRef(null);

  const handleUpload = async () => {
    if (!selectedFile) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('document_type', docType);
      const resp = await fetch(`${API_BASE}/portal/upload/${token}`, { method: 'POST', body: formData });
      if (!resp.ok) throw new Error('Upload failed');
      setSelectedFile(null);
      setDocType('other');
      onSuccess?.();
    } catch { toast.error('Upload failed'); }
    finally { setUploading(false); }
  };

  return (
    <div className="space-y-4">
      <div
        data-testid="vendor-portal-dropzone"
        className="border-2 border-dashed border-border rounded-xl p-6 text-center hover:border-primary/50 transition-colors duration-200 cursor-pointer"
        onClick={() => fileRef.current?.click()}
        onDragOver={e => { e.preventDefault(); e.currentTarget.classList.add('border-primary'); }}
        onDragLeave={e => { e.currentTarget.classList.remove('border-primary'); }}
        onDrop={e => { e.preventDefault(); e.currentTarget.classList.remove('border-primary'); const f = e.dataTransfer.files[0]; if (f) setSelectedFile(f); }}
      >
        <input ref={fileRef} type="file" accept=".pdf,.png,.jpg,.jpeg,.tiff" className="hidden" onChange={e => setSelectedFile(e.target.files?.[0] || null)} />
        {selectedFile ? (
          <div className="flex items-center gap-3 justify-center">
            <FileText className="h-8 w-8 text-primary" />
            <div className="text-left">
              <p className="text-sm font-medium">{selectedFile.name}</p>
              <p className="text-xs text-muted-foreground">{(selectedFile.size / 1024).toFixed(0)} KB</p>
            </div>
          </div>
        ) : (
          <>
            <Upload className="mx-auto h-8 w-8 text-muted-foreground/40 mb-2" />
            <p className="text-sm text-muted-foreground">Click or drag & drop to select file</p>
            <p className="text-xs text-muted-foreground mt-1">PDF, PNG, JPEG, TIFF up to 25MB</p>
          </>
        )}
      </div>
      {selectedFile && (
        <div className="flex items-center gap-3">
          <Select value={docType} onValueChange={setDocType}>
            <SelectTrigger className="flex-1"><SelectValue placeholder="Document type" /></SelectTrigger>
            <SelectContent>
              {['coi','w9','license','workers_comp','auto_insurance','umbrella','bond','safety_document','contract','other'].map(t =>
                <SelectItem key={t} value={t}>{t.toUpperCase().replace('_', ' ')}</SelectItem>
              )}
            </SelectContent>
          </Select>
          <Button data-testid="vendor-portal-upload-document-button" onClick={handleUpload} disabled={uploading}>
            {uploading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />}
            {uploading ? 'Uploading...' : 'Upload'}
          </Button>
        </div>
      )}
    </div>
  );
}
